import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { TrashIcon, FireIcon, ArrowUpRightIcon, ArrowDownRightIcon, PlusIcon } from '@heroicons/react/24/solid';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import './Workout.css';
import { colorFromString, textColorForHsl, hslWithAlpha, normalizeKey } from '../../utils/colorUtils';
import { DurationInput } from "../DurationInput";
import { isWorkoutEditable } from './../../utils/workoutUtils';
import ExerciseDetailModal from './ExerciseDetailModal';
import ExerciseCoachSuggestions, { reconcileExerciseCoachSuggestion } from './ExerciseCoachSuggestions';
import WorkoutProposalBubble from './WorkoutProposalBubble';
import ExerciseCoachComposer from './ExerciseCoachComposer';
import ExerciseCoachMessageAttachments from './ExerciseCoachMessageAttachments';
import CoachAvatar from '../CoachAvatar';
import Input from '../Input';
import { useGeolocationService } from '../../contexts/GeolocationServiceContext';
import { useUnits } from '../../contexts/UnitsContext';
import { lbsToKg, kgToLbs, kmToMiles, milesToKm, metersToFeet, feetToMeters, formatWeight } from '../../utils/units';
import { calculateProjectedWorkoutVolume, calculateProjectedWorkoutScore, calculateEffectiveLoad, isVolumeRecordOccurrence as computeIsVolumeRecord } from '../../utils/volume';
import WorkoutExerciseHistoryChart from './WorkoutExerciseHistoryChart';
import { calculateCompletedHistoryMetricTotals, calculateCompletedWorkingTotals, mergeCurrentWorkoutHistoryPoint } from './workoutExerciseHistoryState';
import { DEFAULT_EXERCISE_CHAT_EXPANDED, exerciseCoachEntryKey, exerciseCoachHistoryKey, historyScrollTop, latestExerciseCoachEntry, latestExerciseCoachMessage, uniqueExerciseCoachEntries, visibleExerciseCoachEntries } from './exerciseCoachConversation';
import { publishWorkoutCoachDiscussionRequest } from './workoutCoachDiscussionRequest';
import { getWorkoutPresentationClasses } from './workoutReadOnlyPresentation';
import { getSharedWorkoutExerciseClass, shouldShowSharedWorkoutCoach } from './sharedWorkoutPresentation';
import { getInitialWorkoutSets } from './sharedWorkoutReadOnlyData';
import { shouldFilterWorkoutSetsByDate, shouldShowWorkoutHistory } from './workoutReadOnlyDisplay';
import { getExerciseImageStyle } from './exerciseImageFraming';
import { canAddWorkoutSet } from './workoutSetActions';
import { coachFieldEffectClass, useDisplayedCoachAppliedFieldEffects } from './coachAppliedFieldEffect';
import { useNewSetRowEffects } from './newSetRowEffect';
import { getWorkoutSetColumns, shouldUseStackedWorkoutSetLayout } from './workoutSetColumns';
import { scrollDownBySetRow } from './setDoneScroll';
import { beginCoachFlow, bindCoachFlowSubject, failCoachFlow, markCoachFlow } from '../../services/coachSequenceTiming';

// Safely parse a date string. Handles two cases:
// 1. "YYYY-MM-DDTHH:mm" or "YYYY-MM-DDTHH:mm:ss" from window.prompt (local time, no
//    timezone suffix) — parsed with individual Date constructor args so it's always
//    treated as local time on all browsers including Safari.
// 2. Full ISO strings from the server (e.g. "...Z") — parsed normally.
const parseSetDate = (str) => {
  if (!str) return null;
  const localMatch = typeof str === 'string' && str.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (localMatch) {
    const [, year, month, day, hours, minutes, seconds = '0'] = localMatch;
    const d = new Date(+year, +month - 1, +day, +hours, +minutes, +seconds);
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

// Helper to get YYYY-MM-DD date key in local timezone (matches Activities.js logic)
const getLocalDateKey = (date) => {
  try {
    return new Date(date).toLocaleDateString('en-CA');
  } catch {
    return new Date(date).toISOString().split('T')[0];
  }
};

// Typewriter effect hook for RPG-style text reveal
const useTypewriter = (text, speed = 80) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const prevTextRef = useRef('');
  
  useEffect(() => {
    // Only trigger typewriter when text changes to something new
    const str = (typeof text === 'string') ? text : (text != null ? String(text) : '');
    if (!str || str === prevTextRef.current) {
      return;
    }
    
    prevTextRef.current = str;
    const words = str.split(' ');
    let currentIndex = 0;
    
    setDisplayedText('');
    setIsTyping(true);
    
    const typeNextWord = () => {
      if (currentIndex < words.length) {
        const newText = words.slice(0, currentIndex + 1).join(' ');
        setDisplayedText(newText);
        currentIndex++;
      } else {
        setIsTyping(false);
        clearInterval(intervalId);
      }
    };
    
    // Type first word immediately
    typeNextWord();
    
    const intervalId = setInterval(typeNextWord, speed);
    
    return () => clearInterval(intervalId);
  }, [text, speed]);
  
  return { displayedText: displayedText || text || '', isTyping };
};



function WorkoutExercise({ workout, workout_exercise, exercises, editable, isOngoing, children, isTemplateMode, backendService, modalService, refreshWorkout, onCoachSuggestionResolved, allowCompletion = false, structureLocked = false, isComplianceCritical = false, recommendation, isLoadingRecommendation = false, isUpdatingRecommendation = false, onSubmitMessage, onAnswerQuestion, onCompletedSetFieldChanged, setDateFilter, readOnly = false, coachImageUrl = null, coachEmoji = '🥋', showInlineCoach = true, proposalAnnotations = [], onApproveProposal, busyStrategyProposalId = null, coachFieldEffects, onCoachFieldEffectDisplayed, coachAddedSetEffects, onCoachAddedSetDisplayed, selectionOrder }) {
  const [imageSrc, setImageSrc] = useState('');
  const [bodyweight, setBodyweight] = useState(readOnly ? null : 180); // Never substitute the viewer's weight for a peer.
  const [bodyweightKeyLoaded, setBodyweightKeyLoaded] = useState(null);
  const [showHistoryChart, setShowHistoryChart] = useState(false);
  const [readOnlyGraphSelected, setReadOnlyGraphSelected] = useState(false);

  // Tracks whether this exercise was already the ongoing/expanded one when the
  // current pointer interaction started, so a selection click on a collapsed
  // exercise never doubles as a detail-screen toggle.
  const wasOngoingAtPointerDownRef = useRef(false);
  const [isSubmittingMessage, setIsSubmittingMessage] = useState(false);
  const [conversationLog, setConversationLog] = useState([]);
  const [showFullChat, setShowFullChat] = useState(DEFAULT_EXERCISE_CHAT_EXPANDED);
  const chatHistoryRef = useRef(null);
  const chatEndRef = useRef(null);
  const uniqueConversationLog = uniqueExerciseCoachEntries(conversationLog);
  const historyKey = exerciseCoachHistoryKey({
    workoutId: workout?._id,
    exerciseId: workout_exercise?._id,
    conversationLength: uniqueConversationLog.length,
  });

  // Fetch conversation log when exercise is selected or recommendation changes
  useEffect(() => {
    if (readOnly || !showInlineCoach) {
      setConversationLog([]);
      return;
    }
    if (workout?._id && workout_exercise?._id) {
      backendService.getExerciseConversation(workout._id, workout_exercise._id)
        .then(data => {
          setConversationLog(data.conversation_log || []);
        })
        .catch(() => {
          // Silently handle errors - empty log is fine
          setConversationLog([]);
        });
    }
  }, [readOnly, showInlineCoach, workout?._id, workout_exercise?._id, backendService, recommendation?.summary]);

  // Keep the expanded history at the newest Coach message from the first paint.
  useEffect(() => {
    if (chatHistoryRef.current && uniqueConversationLog.length > 0 && showFullChat) {
      let settleTimer;
      let transitionTimer;
      requestAnimationFrame(() => {
        const scrollHistoryToBottom = () => {
          const history = chatHistoryRef.current;
          if (history) {
            history.scrollTop = historyScrollTop(history);
          }
        };
        scrollHistoryToBottom();
        // The history's text can finish laying out after the first frame on
        // mobile; anchor once more after that settled layout.
        requestAnimationFrame(scrollHistoryToBottom);
        // Hold the nested viewport at its end through the shell's expansion,
        // not merely before/after it, so no detached intermediate frame shows.
        transitionTimer = setInterval(scrollHistoryToBottom, 16);
        settleTimer = setTimeout(() => {
          clearInterval(transitionTimer);
          scrollHistoryToBottom();
        }, 260);
      });
      return () => {
        clearTimeout(settleTimer);
        clearInterval(transitionTimer);
      };
    }
    return undefined;
  }, [historyKey, uniqueConversationLog.length, showFullChat]);


  const latestCoachMessage = latestExerciseCoachMessage(uniqueConversationLog);
  const latestCoachEntry = latestExerciseCoachEntry(uniqueConversationLog);
  const collapsedCoachText = latestCoachMessage || recommendation?.summary || '';
  const visibleCoachEntries = visibleExerciseCoachEntries({
    conversation: uniqueConversationLog,
    expanded: showFullChat,
    fallbackMessage: recommendation?.summary,
  });

  // Typewriter effect for the latest persisted Coach instruction
  const { isTyping } = useTypewriter(
    collapsedCoachText,
    30 // Speed in ms per word
  );

  // Check if there's a pending question from the AI
  const pendingQuestion = recommendation?.question;
  const questionType = recommendation?.question_type; // 'yes_no', 'choice', or 'open'
  const questionChoices = recommendation?.question_choices;

  // Refresh conversation log from server
  const refreshConversationLog = async () => {
    try {
      const data = await backendService.getExerciseConversation(workout._id, workout_exercise._id);
      setConversationLog(data.conversation_log || []);
    } catch (err) {
      console.error('Failed to refresh conversation log:', err);
    }
  };

  // Handle answering a question (yes/no or choice)
  const handleAnswerQuestion = async (answer) => {
    if (!onAnswerQuestion || isSubmittingMessage) return;
    setIsSubmittingMessage(true);
    try {
      await onAnswerQuestion(workout_exercise._id, answer);
      await refreshConversationLog();
    } catch (error) {
      console.error('Error answering question:', error);
    } finally {
      setIsSubmittingMessage(false);
    }
  };

  // Handle submitting a user message (not in response to a question)
  const handleSubmitUserMessage = async (message, attachments = []) => {
    if (!onSubmitMessage || (!message.trim() && attachments.length === 0) || isSubmittingMessage) return;
    setIsSubmittingMessage(true);
    try {
      await onSubmitMessage(workout_exercise._id, message.trim(), attachments);
      await refreshConversationLog();
    } catch (error) {
      console.error('Error submitting message:', error);
      if (error?.messageSaved) await refreshConversationLog();
      throw error;
    } finally {
      setIsSubmittingMessage(false);
    }
  };

  const handleComposerSubmit = async (message, attachments) => {
    if (pendingQuestion && !isUpdatingRecommendation && attachments.length === 0 && message.trim()) {
      await handleAnswerQuestion(message.trim());
      return;
    }
    await handleSubmitUserMessage(message, attachments);
  };

  // Fetch bodyweight for the workout date
  useEffect(() => {
    if (readOnly) return;
    let cancelled = false;
    const fetchBodyweight = async () => {
      if (workout && workout.created_at) {
        const dayKey = new Date(workout.created_at).toISOString().slice(0, 10);
        if (dayKey === bodyweightKeyLoaded) {
          return; // Already loaded for this day
        }
        try {
          const bw = await backendService.fetchBodyweight(workout.created_at);
          if (cancelled) return;
          setBodyweight(bw);
          setBodyweightKeyLoaded(dayKey);
        } catch (err) {
          console.error('Failed to fetch bodyweight for workout', err);
        }
      }
    };
    fetchBodyweight();
    return () => { cancelled = true; };
  }, [readOnly, workout, backendService, bodyweightKeyLoaded]);

  const queryClient = useQueryClient();
  const [localWorkoutSets, setLocalWorkoutSets] = useState(() =>
    getInitialWorkoutSets({ workoutExercise: workout_exercise, readOnly })
  );
  const [isAdding, setIsAdding] = useState(false);
  const [addSetError, setAddSetError] = useState(null);
  const { position } = useGeolocationService();

  const selectedExercise = useMemo(() => 
    exercises.find(exercise => exercise._id === workout_exercise.exercise_id),
  [exercises, workout_exercise.exercise_id]);

  // Avoid initial ECharts sizing "snap" by delaying mount briefly after selection.
  useEffect(() => {
    let timer;

    if (shouldShowWorkoutHistory({ isOngoing, readOnly, readOnlySelected: readOnlyGraphSelected }) && workout_exercise?.status !== 'removed') {
      setShowHistoryChart(false);
      timer = setTimeout(() => setShowHistoryChart(true), 150);
    } else {
      setShowHistoryChart(false);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isOngoing, readOnly, readOnlyGraphSelected, workout_exercise?._id, workout_exercise?.status]);

  const toggleExerciseDetailScreen= () => {
    const isEditable = isWorkoutEditable(workout);
    const addExercise = isEditable && workout_exercise.status === "removed" ? async () => {
      try {
        await backendService.addExercise(workout._id, workout_exercise.exercise_id);
        refreshWorkout();
      } catch (error) {
        alert('Failed to add exercise. Please try again.');
      }
    } : null;
    const removeExercise = isEditable && workout_exercise.status !== "removed" ? async () => {
      let message = 'Are you sure you want to remove this exercise?';
      if (workout.source?.activity_plan_id && isComplianceCritical) {
        message = "Warning: Removing exercises will reduce your consistency. Do you want to continue?";
      }
      if (window.confirm(message)) {
        try {
          await backendService.removeWorkoutExercise(workout._id, workout_exercise._id);
          refreshWorkout();
        } catch (error) {
          alert('Failed to remove exercise. Please try again.');
        }
      }
    } : null;
    modalService.open(ExerciseDetailModal, {
      exerciseId: workout_exercise.exercise_id,
      backendService: backendService,
      removeExercise: removeExercise,
      addExercise: addExercise,
    });
  }

  // Fetch and set exercise image
  useEffect(() => {
    const fetchImage = async () => {
      const selectedExercise = exercises.find(exercise => exercise._id === workout_exercise.exercise_id);

      if (selectedExercise && selectedExercise.images && selectedExercise.images.length > 0) {
        try {
          const imageReference = selectedExercise.images[1] || selectedExercise.images[0];
          const imagePath = typeof imageReference === 'string' ? imageReference : imageReference?.path;
          const url = await backendService.fetchExerciseImage(imagePath);
          setImageSrc(url);
        } catch (error) {
          console.error('Failed to fetch image:', error);
        }
      } else {
        setImageSrc("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAIAAACRXR/mAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAB3RJTUUH5QQWDBYmV4zfxAAAAAlwSFlzAAAK8AAACvABQqw0mAAAABl0RVh0Q29tbWVudABDcmVhdGVkIHdpdGggR0lNUFeBDhcAAABOSURBVFjD7cEBDQAwDMOg+z+PdmGhKhiRHusNAAImDKU3xkDAAD//9EAAAAAAElFTkSuQmCC");
      }
    };
    fetchImage();
  }, [workout_exercise, exercises, backendService]);

  // Fetch workout sets using react-query
  const { isLoading: isSetsQueryLoading } = useQuery(['workoutSets', workout_exercise._id], () => backendService.fetchWorkoutSets(workout._id, workout_exercise._id), {
    enabled: !readOnly,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      setLocalWorkoutSets(data);
    }
  });
  const isLoading = !readOnly && isSetsQueryLoading;

  // When displaying a multipart workout, only show sets completed on the target date
  const displaySets = useMemo(() => {
    if (!shouldFilterWorkoutSetsByDate({ readOnly, setDateFilter })) return localWorkoutSets;
    return localWorkoutSets.filter(s =>
      s.complete && s.completed_at && getLocalDateKey(s.completed_at) === setDateFilter
    );
  }, [localWorkoutSets, readOnly, setDateFilter]);
  const displayedCoachFieldEffects = useDisplayedCoachAppliedFieldEffects({
    effects: coachFieldEffects,
    displayedExercises: [{ ...workout_exercise, sets: displaySets }],
    onDisplayed: onCoachFieldEffectDisplayed,
  });

  const { data: historyData } = useQuery(
    ['workoutExerciseHistory', workout_exercise._id],
    () => backendService.fetchWorkoutExerciseHistory(workout_exercise._id),
    {
      enabled: !readOnly && Boolean(workout_exercise?._id),
      retry: (failureCount, error) => error?.response?.status !== 503 && failureCount < 3,
      refetchOnWindowFocus: false
    }
  );

  const newSetRows = useNewSetRowEffects([workout?._id, workout_exercise._id], [{ ...workout_exercise, sets: displaySets }], readOnly, coachAddedSetEffects, onCoachAddedSetDisplayed);

  useEffect(() => { setIsAdding(false); setAddSetError(null); }, [workout?._id, workout_exercise._id, readOnly]);

  // Display only canonical successful additions: no temporary ID/remount and
  // failed requests cannot leave a phantom row or overwrite intervening edits.
  const addMutation = useMutation(newSet => backendService.addWorkoutSet(newSet), {
    onMutate: async () => {
      const owner = newSetRows.capture();
      setAddSetError(null);
      setIsAdding(true);
      await queryClient.cancelQueries(['workoutSets', workout_exercise._id]);
      return { owner };
    },
    onError: (err, variables, context) => {
      console.error('Error adding workout set:', err);
      if (newSetRows.isCurrent(context?.owner)) setAddSetError('Could not add set. Please try again.');
    },
    onSuccess: (data, newSet, context) => {
      if (!newSetRows.isCurrent(context?.owner) || !data?._id) return;
      newSetRows.publish(newSet.workout_exercise_id, data._id, context.owner);
      setLocalWorkoutSets(old => old.some(set => set._id === data._id) ? old : [...old, data]);
    },
    onSettled: (data, err, variables, context) => {
      if (newSetRows.isCurrent(context?.owner)) setIsAdding(false);
      queryClient.invalidateQueries(['workoutSets', variables.workout_exercise_id]);
      queryClient.invalidateQueries(['workoutExerciseHistory', variables.workout_exercise_id]);
    }
  });

  // Mutation to remove a workout set
  const removeMutation = useMutation(
    (setId) => backendService.removeWorkoutSet(setId, {
      workoutId: workout?._id,
      workoutExerciseId: workout_exercise?._id
    }),
    {
    onMutate: async (setId) => {
      await queryClient.cancelQueries(['workoutSets', workout_exercise._id]);

      const removedIndex = localWorkoutSets.findIndex(set => set._id === setId);
      const removedSet = localWorkoutSets[removedIndex];
      setLocalWorkoutSets((old) => old.filter(set => set._id !== setId));
      return { removedSet, removedIndex };
    },
    onError: (err, setId, context) => {
      console.error('Error removing workout set:', err);
      setLocalWorkoutSets(current => {
        if (!context?.removedSet || current.some(set => set._id === setId)) return current;
        const restored = [...current];
        restored.splice(Math.min(context.removedIndex, restored.length), 0, context.removedSet);
        return restored;
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries(['workoutSets', workout_exercise._id]);

      // Removal can affect history aggregates.
      queryClient.invalidateQueries(['workoutExerciseHistory', workout_exercise._id]);
    }
  });

  // Handle changes to workout set fields
  const handleSetChange = (setId, updates) => {
    if (updates.complete) {
      const coachFlow = beginCoachFlow('set_review', typeof window !== 'undefined' ? window.event?.timeStamp : undefined);
      bindCoachFlowSubject(coachFlow, String(workout_exercise?._id || ''));
      const previousSet = localWorkoutSets.find(set => set._id === setId);
      const completionUpdates = structureLocked ? { complete: true } : updates;
      const reconcileFailedCompletion = (error) => {
        console.error('Error updating workout set:', error);
        setLocalWorkoutSets(currentSets => currentSets.map(set =>
          set._id === setId
            ? { ...set, complete: previousSet?.complete || false, completed_at: previousSet?.completed_at }
            : set
        ));
        queryClient.invalidateQueries(['workoutSets', workout_exercise._id]);
        if (typeof refreshWorkout === 'function') refreshWorkout();
      };

      // Use the already-watched location
      if (position && !structureLocked) {
        console.log(`Reusing cached position: lat=${position.latitude}, lng=${position.longitude}`);
        const updatedSet = {
          ...updates,
          completed_at: new Date().toISOString(),
          coords: {
            latitude: position.latitude,
            longitude: position.longitude,
            accuracy: position.accuracy
          }
        };
        // Update local state immediately
        const updatedSets = localWorkoutSets.map(set =>
          set._id === setId ? { ...set, ...updatedSet } : set
        );
        setLocalWorkoutSets(updatedSets);

        // Immediately persist to backend and refresh workout so parent can show map
        markCoachFlow(coachFlow, 'presubmit_end');
        markCoachFlow(coachFlow, 'save_start');
        backendService.updateWorkoutSet(setId, updatedSet, {
          workoutId: workout?._id,
          workoutExerciseId: workout_exercise?._id
        })
          .then(() => {
            markCoachFlow(coachFlow, 'save_end');
            if (typeof refreshWorkout === 'function') refreshWorkout();

            // Completion/field changes affect chart.
            queryClient.invalidateQueries(['workoutExerciseHistory', workout_exercise._id]);
          })
          .catch(error => { failCoachFlow(coachFlow); return reconcileFailedCompletion(error); });
      } else {
        // Structure-locked replacements use the backend's boolean-only completion
        // contract; normal workouts fall back here when location is unavailable.
        if (!structureLocked) {
          console.warn('GeolocationService: position not available yet.');
        }
        const updatedSets = localWorkoutSets.map(set =>
          set._id === setId ? { ...set, ...completionUpdates, completed_at: new Date().toISOString() } : set
        );
        setLocalWorkoutSets(updatedSets);

        markCoachFlow(coachFlow, 'presubmit_end');
        markCoachFlow(coachFlow, 'save_start');
        backendService.updateWorkoutSet(setId, completionUpdates, {
          workoutId: workout?._id,
          workoutExerciseId: workout_exercise?._id,
          ...(structureLocked ? { separateWorkoutSetFields: true } : {}),
        })
          .then(() => {
            markCoachFlow(coachFlow, 'save_end');
            if (typeof refreshWorkout === 'function') refreshWorkout();

            // Completion/field changes affect chart.
            queryClient.invalidateQueries(['workoutExerciseHistory', workout_exercise._id]);
          })
          .catch(error => { failCoachFlow(coachFlow); return reconcileFailedCompletion(error); });
      }
    } else {
      // Standard logic for non-complete updates
      const updatedSets = localWorkoutSets.map(set =>
        set._id === setId ? { ...set, ...updates } : set
      );
      setLocalWorkoutSets(updatedSets);

      // If the set is already complete, notify parent so AI recommendation can refresh
      const existingSet = localWorkoutSets.find(s => s._id === setId);
      if (existingSet?.complete && onCompletedSetFieldChanged) {
        onCompletedSetFieldChanged(workout_exercise._id);
      }

      debouncedUpdateSet(setId, updates);
    }
  };

  // Field edits are buffered durably by BackendService: they are merged per set,
  // persisted before the request, and replayed after a background/offline gap.
  // A rejection here means the server refused the edit outright, so fall back to
  // server truth rather than leaving a value on screen that was never saved.
  const debouncedUpdateSet = useCallback((setId, updates) => {
    backendService.updateWorkoutSet(setId, updates, {
      workoutId: workout?._id,
      workoutExerciseId: workout_exercise?._id,
      ...(structureLocked ? { separateWorkoutSetFields: true } : {}),
    })
      .then(() => {
        if (typeof refreshWorkout === 'function') refreshWorkout();
        queryClient.invalidateQueries(['workoutExerciseHistory', workout_exercise._id]);
      })
      .catch(error => {
        console.error('Error updating workout set:', error);
        queryClient.invalidateQueries(['workoutSets', workout_exercise._id]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendService, queryClient, workout?._id, workout_exercise._id, structureLocked]);

  // Add a new workout set
  const handleAddWorkoutSet = () => {
    if (readOnly || isAdding) return;
    const lastSet = localWorkoutSets.length > 0 ? localWorkoutSets[localWorkoutSets.length - 1] : null;
    let newSet = { activity_id: workout?._id, workout_exercise_id: workout_exercise._id };

    newSet.weight = lastSet ? lastSet.weight : undefined;
    newSet.repetitions = lastSet ? lastSet.repetitions : undefined;
    newSet.distance = lastSet ? lastSet.distance : undefined;
    newSet.duration = lastSet ? lastSet.duration : undefined;
    newSet.calories = lastSet ? lastSet.calories : undefined;
    newSet.elevation_gain = lastSet ? lastSet.elevation_gain : undefined;
    newSet.pack_weight = lastSet ? lastSet.pack_weight : undefined;
    newSet.perceived_effort = lastSet ? lastSet.perceived_effort : undefined;

    addMutation.mutate(newSet);
  };

  // Remove a workout set
  const handleRemoveWorkoutSet = (setId) => {
    removeMutation.mutate(setId);
  };

  // Get the current exercise and its category
  const currentExercise = exercises.find(ex => ex._id === workout_exercise.exercise_id);
  // Prefer the stable exercise id when available so title color exactly matches SetMap.
  const canonicalTitleKey = workout_exercise && workout_exercise.exercise_id ? String(workout_exercise.exercise_id) : (currentExercise?.name || '');
  const _titleColor = colorFromString(normalizeKey(canonicalTitleKey));
  const _titleTextColor = textColorForHsl(_titleColor);
  // Use low opacity (10%) for the title background, and full opacity for the border
  const _titleBg = hslWithAlpha(_titleColor, 0.10);
  const _titleBorder = hslWithAlpha(_titleColor, 1.0);
  const category = currentExercise?.category.toLowerCase() || 'strength';
  const exerciseMeta = useMemo(() => currentExercise || {}, [currentExercise]);
  const unknownOwnerLoad = readOnly && (!currentExercise || Number(exerciseMeta.bodyWeightLoadFactor) > 0 || exerciseMeta.estimateCaloriesForVolume);

  const exerciseColorVars = {
    '--exercise-bg': _titleBg,
    '--exercise-border': _titleBorder,
    '--exercise-fg': _titleTextColor
  };

  let columns = getWorkoutSetColumns(exerciseMeta);

  if (isTemplateMode) {
    columns = columns.filter(col => col.field !== 'weight');
  }

  if (allowCompletion && !isTemplateMode) {
    columns.push({ header: 'Done', field: 'complete', type: 'checkbox' });
  }

  const showSetActions = !structureLocked && ((isWorkoutEditable(workout) && isOngoing) || isTemplateMode);
  const hasDurationColumn = columns.some(col => col.type === 'duration');
  const renderedSetColumnCount = columns.length + (showSetActions ? 1 : 0);
  const useStackedSetLayout = shouldUseStackedWorkoutSetLayout(columns);

  // Read selected units from context and decorate headers/values
  const { weightUnit, distanceUnit, elevationUnit } = useUnits();

  columns = columns.map((col) => {
    if (col.unitKind === 'weight' || col.field === 'weight') {
      return { ...col, header: `${col.header} (${weightUnit === 'lbs' ? 'lbs' : 'kg'})` };
    }
    if (col.unitKind === 'distance' || col.field === 'distance') {
      return { ...col, header: `${col.header} (${distanceUnit === 'km' ? 'km' : 'mi'})` };
    }
    if (col.unitKind === 'elevation') {
      return { ...col, header: `${col.header} (${elevationUnit})` };
    }
    return col;
  });

  const areAllSetsComplete = () => {
    if (displaySets.length === 0 || isTemplateMode) {
      return false;
    }

    if (workout_exercise.status === 'removed') {
      return false;
    }

    return displaySets.every(set => {
      return set.complete !== undefined && set.complete;
    });
  };

  const allSetsComplete = areAllSetsComplete();

  // Determine which set is the "current" one (first incomplete) and "last completed" (for undo)
  const firstIncompleteIndex = displaySets.findIndex(set => !set.complete);
  const lastCompletedIndex = firstIncompleteIndex === -1
    ? displaySets.length - 1  // all complete → last set is last completed
    : firstIncompleteIndex - 1;    // the one right before the first incomplete

  const normalizeDurationToSeconds = useCallback((value) => {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'number') {
      return value >= 1000 ? value / 1000 : value;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }

      if (trimmed.includes(':')) {
        const parts = trimmed.split(':').map((part) => parseFloat(part) || 0);
        while (parts.length < 3) {
          parts.unshift(0);
        }
        const [hours, minutes, seconds] = parts;
        return (hours * 3600) + (minutes * 60) + seconds;
      }

      const numericValue = parseFloat(trimmed);
      if (!Number.isFinite(numericValue)) {
        return null;
      }

      if (trimmed.toLowerCase().endsWith('ms')) {
        return numericValue / 1000;
      }

      return numericValue >= 1000 ? numericValue / 1000 : numericValue;
    }

    if (typeof value === 'object') {
      const hours = parseFloat(value.hours) || 0;
      const minutes = parseFloat(value.minutes) || 0;
      const seconds = parseFloat(value.seconds) || 0;
      if (hours === 0 && minutes === 0 && seconds === 0) {
        return null;
      }
      return (hours * 3600) + (minutes * 60) + seconds;
    }

    return null;
  }, []);

  const getComparableValue = useCallback((set, field) => {
    if (!set || typeof field !== 'string') {
      return null;
    }

    const rawValue = set[field];
    if (rawValue === null || rawValue === undefined) {
      return null;
    }

    if (field === 'duration') {
      return normalizeDurationToSeconds(rawValue);
    }

    const numericValue = typeof rawValue === 'number' ? rawValue : parseFloat(rawValue);
    return Number.isFinite(numericValue) ? numericValue : null;
  }, [normalizeDurationToSeconds]);

  const highlightMeta = useMemo(() => {
    const sets = Array.isArray(displaySets) ? displaySets : [];
    const meta = {
      warmupField: null,
      warmupIndex: null,
      trends: sets.map(() => ({}))
    };

    if (!sets.length) {
      return meta;
    }

    const determineWarmupField = () => {
      if (unknownOwnerLoad) return null;
      const firstSet = sets[0];
      if (!firstSet) {
        return null;
      }
      
      // Use effective load for warmup detection (handles bodyweight exercises with assistance)
      const exerciseMeta = selectedExercise || {};
      const effectiveLoads = sets.map(set => calculateEffectiveLoad(set, exerciseMeta, bodyweight));
      const firstLoad = effectiveLoads[0];
      const otherLoads = effectiveLoads.slice(1).filter(l => l > 0);
      
      if (otherLoads.length > 0) {
        // First set is warmup if it has no effective load or is <=80% of all subsequent loaded sets
        const isWarmup = firstLoad <= 0 || otherLoads.every(l => firstLoad <= l * 0.8);
        if (isWarmup) {
          return 'weight'; // Return 'weight' as the field for styling purposes
        }
      }
      
      // Fallback: check duration for cardio/timed exercises
      const candidateFields = ['duration'];
      for (const field of candidateFields) {
        const firstValue = getComparableValue(firstSet, field);
        if (firstValue === null) {
          continue;
        }
        const otherValues = sets.slice(1)
          .map((set) => getComparableValue(set, field))
          .filter((value) => value !== null && value > 0);
        if (!otherValues.length) {
          continue;
        }
        const isWarmup = firstValue <= 0 || otherValues.every((value) => firstValue <= value * 0.8);
        if (isWarmup) {
          return field;
        }
      }
      return null;
    };

    const warmupField = determineWarmupField();
    const warmupIndex = typeof warmupField === 'string' ? 0 : null;

    const trendFields = ['weight', 'duration'];
    const trends = sets.map(() => ({}));

    trendFields.forEach((field) => {
      for (let i = 1; i < sets.length; i += 1) {
        const currentValue = getComparableValue(sets[i], field);
        const previousValue = getComparableValue(sets[i - 1], field);
        if (currentValue === null || previousValue === null) {
          continue;
        }
        if (warmupField === field && warmupIndex === i - 1) {
          continue;
        }
        if (currentValue > previousValue) {
          trends[i][field] = 'up';
        } else if (currentValue < previousValue) {
          trends[i][field] = 'down';
        }
      }
    });

    return { warmupField, warmupIndex, trends };
  }, [displaySets, getComparableValue, selectedExercise, bodyweight, unknownOwnerLoad]);

  const projectedVolume = useMemo(() => {
    if (unknownOwnerLoad || allSetsComplete) {
      return null;
    }

    if (!Array.isArray(displaySets) || displaySets.length === 0) {
      return null;
    }

    const projected = calculateProjectedWorkoutVolume(displaySets, exerciseMeta, bodyweight || 0);
    // Return the total projected volume based on inputs (even if not complete) 
    // so the "yellow ghost" marker can display the plan.
    return Number.isFinite(projected) && projected > 0 ? projected : null;
  }, [allSetsComplete, displaySets, exerciseMeta, bodyweight, unknownOwnerLoad]);

  const completedWorkingTotals = useMemo(() => unknownOwnerLoad ? { volume: null, score: null } : calculateCompletedWorkingTotals(
    displaySets,
    exerciseMeta,
    bodyweight || 0,
    highlightMeta.warmupIndex
  ), [displaySets, exerciseMeta, bodyweight, highlightMeta.warmupIndex, unknownOwnerLoad]);
  const completedVolume = completedWorkingTotals.volume;

  const projectedScore = useMemo(() => {
    if (unknownOwnerLoad || allSetsComplete) {
      return null;
    }

    if (!Array.isArray(displaySets) || displaySets.length === 0) {
      return null;
    }

    const projected = calculateProjectedWorkoutScore(displaySets, exerciseMeta, bodyweight || 0);
    return Number.isFinite(projected) && projected > 0 ? projected : null;
  }, [allSetsComplete, displaySets, exerciseMeta, bodyweight, unknownOwnerLoad]);

  const completedScore = completedWorkingTotals.score;
  // Match the chart's live completed score instead of waiting for history to
  // refetch after the final checkbox (or a completed-set edit).
  const isVolumeRecordOccurrence = useMemo(() => {
    if (readOnly) return false;
    const history = mergeCurrentWorkoutHistoryPoint(historyData?.history, {
      category,
      currentWorkoutId: workout?._id,
      currentWorkoutDate: workout?.created_at,
      completedScore,
    });
    return computeIsVolumeRecord(history, category, workout);
  }, [readOnly, historyData?.history, category, workout, completedScore]);

  const completedHistoryMetricTotals = useMemo(
    () => calculateCompletedHistoryMetricTotals(displaySets),
    [displaySets]
  );

  const renderWithIndicators = (field, setIndex, content) => {
    if (!field && field !== 0) {
      return content;
    }

    const badges = [];
    if (
      highlightMeta.warmupField === field &&
      highlightMeta.warmupIndex === setIndex
    ) {
      badges.push(
        <span key="warmup" className="set-indicator warmup" title="Warm-up set">
          <FireIcon aria-hidden="true" />
        </span>
      );
    }

    const trend = highlightMeta.trends[setIndex]?.[field];
    if (trend === 'up') {
      badges.push(
        <span key="trend-up" className="set-indicator overload" title="Progressive overload">
          <ArrowUpRightIcon aria-hidden="true" />
        </span>
      );
    } else if (trend === 'down') {
      badges.push(
        <span key="trend-down" className="set-indicator decrease" title="Reduced load">
          <ArrowDownRightIcon aria-hidden="true" />
        </span>
      );
    }

    return (
      <div className="set-value-with-indicator">
        {content}
        {badges.length > 0 && (
          <span className="set-indicator-stack">{badges}</span>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="workout-exercise-parent loading">
        <div className="exercise-loading-container">
          <div className="exercise-loading-text">
            {selectedExercise?.name ? selectedExercise.name : 'Loading Exercise'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`workout-exercise-parent
        ${workout_exercise.status === 'removed' ? 'removed' : ''}
        ${isOngoing ? 'active' : ''}
        ${getWorkoutPresentationClasses({
          readOnly,
          allSetsComplete,
          isVolumeRecord: isVolumeRecordOccurrence
        })}
        ${getSharedWorkoutExerciseClass({ readOnly, workoutStatus: workout.status, status: workout_exercise.status })}`}
      data-exercise-id={workout_exercise && workout_exercise._id}
      style={exerciseColorVars}
    >
      <div className="exercise-video-background">
        <div
          className="exercise-overlay"
          style={{
            backgroundImage: `url(${imageSrc})`,
            ...getExerciseImageStyle(selectedExercise?.images?.[1] || selectedExercise?.images?.[0], selectedExercise).style,
          }}
        />
        <div className="exercise-overlay-darken" />
      </div>
      <div className="exercise-container">
        <div className="exercise-name">
          <div
            className={`exercise-title ${isOngoing || readOnlyGraphSelected ? 'selected' : ''}`}
            onPointerDown={() => { wasOngoingAtPointerDownRef.current = isOngoing; }}
            {...((isOngoing || readOnly || isTemplateMode) && workout_exercise.status !== 'removed' ? {
              role: 'button',
              tabIndex: 0,
              onClick: () => {
                // Template exercises are already selected by definition; their title
                // is the direct entry point to details and plan-level removal.
                if (isTemplateMode) {
                  toggleExerciseDetailScreen();
                  return;
                }
                // The row is focused/selected on pointerdown, so by the time this
                // click fires the exercise may have *just* become the ongoing one.
                if (readOnly) {
                  setReadOnlyGraphSelected((selected) => !selected);
                  return;
                }
                // Only toggle details when it was already ongoing before this
                // interaction started; otherwise this click is the selection click.
                if (!wasOngoingAtPointerDownRef.current) return;
                toggleExerciseDetailScreen();
              },
              onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (readOnly) {
                    setReadOnlyGraphSelected((selected) => !selected);
                  } else {
                    toggleExerciseDetailScreen();
                  }
                }
              }
            } : {})}
          >
            <h4 style={{ margin: 0 }}>
              {selectionOrder && <span className="superset-selection-order" data-exercise-id={workout_exercise._id} aria-hidden="true">{selectionOrder}</span>}
              {currentExercise?.name || "No exercise selected"}
            </h4>
          </div>
          {proposalAnnotations.map(annotation => (
            <WorkoutProposalBubble
              key={`${annotation.proposalId}:${annotation.type}`}
              annotation={annotation}
              onApprove={onApproveProposal}
              disabled={String(busyStrategyProposalId || '') === String(annotation.proposalId)}
            />
          ))}
        </div>

        {/* AI Coach Chat */}
        {showInlineCoach && shouldShowSharedWorkoutCoach({ readOnly, workoutStatus: workout.status }) && isOngoing && workout_exercise.status !== 'removed' && (onSubmitMessage || recommendation || isLoadingRecommendation) && (
          <div className={`exercise-coach-chat${showFullChat ? ' chat-expanded' : ''}`}>
            {/* Chat history toggle */}
            {conversationLog.length > 0 && (
              <button 
                className="chat-history-toggle"
                onClick={() => setShowFullChat(!showFullChat)}
              >
                {showFullChat ? 'Hide previous messages' : `Show previous messages (${conversationLog.length})`}
              </button>
            )}

            {/* One stable Coach timeline: collapsed mode keeps only the newest
                instruction; expanded mode reveals previous entries in place. */}
            {(visibleCoachEntries.length > 0 || isLoadingRecommendation || isUpdatingRecommendation) && (
              <div className={`chat-history${showFullChat ? ' chat-history-expanded' : ' chat-history-collapsed'}`} ref={chatHistoryRef}>
                {visibleCoachEntries.map((entry, idx) => (
                  <div key={exerciseCoachEntryKey(entry, idx)} className={`chat-message chat-message-${entry.type}${entry === latestCoachEntry ? ' chat-message-latest-coach' : ''}`}>
                    {(entry.type === 'ai_recommendation' || entry.type === 'ai_message') && (
                      <>
                        <CoachAvatar imageUrl={coachImageUrl} emoji={coachEmoji} identitySettled alt="Coach" className="chat-icon coach-chat-image" />
                        <span className="chat-content">{entry.data?.message || entry.content}</span>
                      </>
                    )}
                    {entry.type === 'ai_question' && (
                      <>
                        <CoachAvatar imageUrl={coachImageUrl} emoji={coachEmoji} identitySettled alt="Coach" className="chat-icon coach-chat-image" />
                        <span className="chat-content">{entry.data?.question || entry.data?.message || entry.content}</span>
                      </>
                    )}
                    {(entry.type === 'user_response' || entry.type === 'user_message') && (
                      <>
                        <span className="chat-content user">{entry.data?.message || (entry.data?.attachments?.length ? 'Photo attached' : entry.content)}</span>
                        <ExerciseCoachMessageAttachments attachments={entry.data?.attachments} backendService={backendService} />
                        <span className="chat-icon">👤</span>
                      </>
                    )}
                    {entry.type === 'set_complete' && (
                      <span className="chat-content set-complete">
                        ✓ Set {(entry.data?.set_index ?? 0) + 1}: {entry.data?.weight > 0 ? `${entry.data.weight} × ` : ''}{entry.data?.repetitions > 0 ? `${entry.data.repetitions} reps` : ''}{entry.data?.duration > 0 ? `${Math.round(entry.data.duration / 1000)}s` : ''}
                      </span>
                    )}
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
            )}

            {/* Pending question text (hidden when updating) */}
            {pendingQuestion && !isTyping && !isUpdatingRecommendation && (
              <div className="chat-question-text">
                <span className="chat-icon">🤖</span>
                <span className="chat-content">{pendingQuestion}</span>
              </div>
            )}

            {/* Chat input with quick answers inline */}
            {onSubmitMessage && workout.status !== 'complete' && (
              <div className="chat-input-row">
                {/* Quick answer buttons (shown when there's a pending question and not updating) */}
                {pendingQuestion && !isTyping && !isUpdatingRecommendation && questionType !== 'open' && (
                  <div className="chat-quick-answers">
                    {questionType === 'yes_no' && (
                      <>
                        <button 
                          className="quick-answer-btn"
                          onClick={() => handleAnswerQuestion('Yes')}
                          disabled={isSubmittingMessage}
                        >
                          Yes
                        </button>
                        <button 
                          className="quick-answer-btn"
                          onClick={() => handleAnswerQuestion('No')}
                          disabled={isSubmittingMessage}
                        >
                          No
                        </button>
                      </>
                    )}
                    {questionType === 'choice' && questionChoices && questionChoices.map((choice, idx) => (
                      <button 
                        key={idx}
                        className="quick-answer-btn"
                        onClick={() => handleAnswerQuestion(choice)}
                        disabled={isSubmittingMessage}
                      >
                        {choice}
                      </button>
                    ))}
                    <button 
                      className="quick-answer-btn skip"
                      onClick={() => handleAnswerQuestion('[skipped]')}
                      disabled={isSubmittingMessage}
                    >
                      Skip
                    </button>
                  </div>
                )}
                <ExerciseCoachComposer
                  onSubmit={handleComposerSubmit}
                  disabled={isSubmittingMessage}
                  resetKey={pendingQuestion || ''}
                  placeholder={pendingQuestion && !isUpdatingRecommendation ? 'Other, or ask something else...' : 'Ask coach...'}
                />
              </div>
            )}
          </div>
        )}

        {shouldShowSharedWorkoutCoach({ readOnly, workoutStatus: workout.status }) && workout_exercise.status !== 'removed' && (
          <ExerciseCoachSuggestions
            recommendation={recommendation}
            workoutId={workout._id}
            workoutExerciseId={workout_exercise._id}
            workoutStatus={workout.status}
            readOnly={readOnly}
            editable={editable}
            exerciseName={currentExercise?.name}
            coachImageUrl={coachImageUrl}
            coachEmoji={coachEmoji}
            backendService={backendService}
            onResolved={(resolution) => reconcileExerciseCoachSuggestion({
              refreshWorkout: onCoachSuggestionResolved || refreshWorkout,
              workoutExerciseId: workout_exercise._id,
              invalidateSets: async (targetId) => {
                const key = ['workoutSets', targetId || workout_exercise._id];
                await queryClient.cancelQueries(key, { exact: true });
                return queryClient.invalidateQueries(key, { exact: true });
              },
              resolution,
            })}
            onDiscuss={publishWorkoutCoachDiscussionRequest}
          />
        )}

        {shouldShowWorkoutHistory({ isOngoing, readOnly, readOnlySelected: readOnlyGraphSelected }) && workout_exercise.status !== 'removed' && (
          <div className={`exercise-history-chart-wrapper ${showHistoryChart ? 'is-visible' : ''}`}
          >
            {showHistoryChart && (
              <WorkoutExerciseHistoryChart
                workoutExerciseId={workout_exercise._id}
                category={category}
                backendService={backendService}
                currentWorkoutId={workout?._id}
                currentWorkoutDate={workout?.created_at}
                projectedVolume={projectedVolume}
                completedVolume={completedVolume}
                projectedScore={projectedScore}
                completedScore={completedScore}
                completedDistance={completedHistoryMetricTotals.distance}
                completedDurationSeconds={completedHistoryMetricTotals.durationSeconds}
                completedCalories={completedHistoryMetricTotals.calories}
                completedRepetitions={completedHistoryMetricTotals.repetitions}
                allSetsComplete={allSetsComplete}
              />
            )}
          </div>
        )}

        <table className={`activity-table exercise-set-table${hasDurationColumn ? ' has-duration' : ''}${hasDurationColumn && !useStackedSetLayout && renderedSetColumnCount >= 3 ? ' has-constrained-duration' : ''}${useStackedSetLayout ? ' is-stacked-mobile' : ''}${showSetActions ? ' has-set-actions' : ''}`}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.field}
                  className={`exercise-set-header exercise-set-${col.type}`}
                  data-unit-kind={col.unitKind || (col.field === 'weight' || col.field === 'distance' ? col.field : undefined)}
                  style={{ textAlign: "center" }}
                >
                  {col.header}
                </th>
              ))}
              {showSetActions && (
                <th
                  className="exercise-set-header exercise-set-action"
                  style={{ textAlign: "center" }}
                ></th>
              )}
            </tr>
          </thead>
          <tbody>
            {displaySets.map((set, index) => {
              const isCurrentSet = index === firstIncompleteIndex;
              const isLastCompleted = index === lastCompletedIndex;
              const isSetInteractive = isCurrentSet || isLastCompleted;
              return (
              <React.Fragment key={set._id || `${workout_exercise._id}-${index}`}>
              <tr className={`${isCurrentSet ? 'current-set-row' : ''}${newSetRows.className(workout_exercise._id, set._id)}`} data-shortcut-set-index={index}>
                {columns.map((col) => (
                  <td
                    key={`${set._id || index}-${col.field}`}
                    className={`exercise-set-cell exercise-set-${col.type}${coachFieldEffectClass(displayedCoachFieldEffects, workout_exercise._id, set._id, col.field)}`}
                    data-field={col.field}
                    data-label={col.header}
                    aria-label={col.header}
                    data-unit-kind={col.unitKind || (col.field === 'weight' || col.field === 'distance' ? col.field : undefined)}
                    style={{ textAlign: "center" }}
                  >
                    {col.type === "checkbox" ? (
                      <div className="set-checkbox-wrapper">
                        {editable ? (
                          <input
                            className={`${col.field}-field${isCurrentSet ? ' current-set-checkbox' : ''} ${!isSetInteractive ? 'set-checkbox-readonly' : ''}`}
                            type="checkbox"
                            checked={set[col.field] || false}
                            onChange={(e) => {
                              if (!isSetInteractive) return;
                              if (e.target.checked && index < displaySets.length - 1) {
                                scrollDownBySetRow(e.currentTarget);
                              }
                              handleSetChange(set._id, { [col.field]: e.target.checked })
                            }}
                            disabled={isAdding || isTemplateMode}
                          />
                        ) : set[col.field] ? (
                          <CheckIcon className="checkmark" />
                        ) : isWorkoutEditable(workout) ? (
                          <span>-</span>
                        ) : (
                          <XMarkIcon className="cross" />
                        )}
                        {set.complete && (() => {
                          const base = set.completed_at || workout.completed_at;
                          const d = parseSetDate(base);
                          if (!d) return null;
                          const localIso = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
                          if (structureLocked) {
                            return (
                              <span className="set-completed-at-label" title="Completion time">
                                {d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            );
                          }
                          const saveTime = (newVal) => {
                            const parsed = parseSetDate(newVal);
                            if (!parsed) return;
                            backendService.updateWorkoutSet(set._id, { completed_at: parsed.toISOString() }, {
                              workoutId: workout?._id,
                              workoutExerciseId: workout_exercise?._id,
                              workoutStatus: workout?.status,
                              source: 'workout_exercise_completed_at_edit'
                            })
                              .then(() => {
                                setLocalWorkoutSets(localWorkoutSets.map(s =>
                                  s._id === set._id ? { ...s, completed_at: parsed.toISOString() } : s
                                ));
                                if (typeof refreshWorkout === 'function') refreshWorkout();
                              })
                              .catch(err => console.error('Error updating set time:', err));
                          };
                          return (
                            <span
                              className="set-completed-at-label"
                              title="Tap to adjust completion time"
                              onClick={() => {
                                const input = document.getElementById(`set-time-input-${set._id}`);
                                if (input) { input.value = localIso; input.showPicker?.(); }
                              }}
                            >
                              {d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              <input
                                id={`set-time-input-${set._id}`}
                                type="datetime-local"
                                className="set-completed-at-input"
                                defaultValue={localIso}
                                onChange={(e) => {
                                  if (e.target.value && e.target.value !== localIso) saveTime(e.target.value);
                                }}
                              />
                            </span>
                          );
                        })()}
                      </div>
                    ) : col.type === "duration" ? (
                      renderWithIndicators(
                        col.field,
                        index,
                        <DurationInput
                          value={set[col.field]}
                          min={col.min}
                          step={col.step}
                          onChange={(value) =>
                            handleSetChange(set._id, { [col.field]: value })
                          }
                          onAccept={(value) => handleSetChange(
                            set._id,
                            structureLocked ? { complete: true } : { [col.field]: value, complete: true }
                          )}
                          disabled={isAdding || isTemplateMode}
                          editable={editable}
                          modalService={modalService}
                          enableTimer={!isTemplateMode && (workout.status === 'ongoing') && isCurrentSet}
                          allowTimerWhenReadOnly={structureLocked && !readOnly && allowCompletion}
                          isCurrentSet={isCurrentSet}
                          imageSrc={imageSrc}
                          title={selectedExercise ? selectedExercise.name : ''}
                        />
                      )
                    ) : (
                      (() => {
                        // For numeric fields we may need to convert display units
                        if (col.unitKind === 'weight' || col.field === 'weight') {
                          // DB stores lbs. Convert to selected unit for display.
                          // Always format to 1 decimal max for clean display
                          const rawVal = (weightUnit === 'kg') ? lbsToKg(set[col.field]) : set[col.field];
                          const displayVal = formatWeight(rawVal);
                          return renderWithIndicators(
                            col.field,
                            index,
                            <Input
                              preserveFocusedDraft
                              value={displayVal}
                              min={col.min}
                              max={col.max}
                              step={col.step}
                              type={col.type}
                              className={`${col.field}-field`}
                              onChange={(value) => {
                                // Convert back to DB unit (lbs) before sending
                                const toStore = (weightUnit === 'kg') ? kgToLbs(value) : value;
                                handleSetChange(set._id, { [col.field]: toStore });
                              }}
                              disabled={isAdding}
                              editable={editable}
                              clearWorkoutCoachOnFocus
                              showPlateCalculator={col.field === 'weight'}
                              plateStorageKey={set._id}
                            />
                          );
                        }

                        if (col.unitKind === 'distance' || col.field === 'distance') {
                          // DB stores km. Convert to selected unit for display.
                          const displayVal = (distanceUnit === 'mi') ? kmToMiles(set[col.field]) : set[col.field];
                          return (
                            <Input
                              preserveFocusedDraft
                              value={displayVal}
                              min={col.min}
                              max={col.max}
                              step={col.step}
                              type={col.type}
                              className={`${col.field}-field`}
                              onChange={(value) => {
                                // Convert back to DB unit (km) before sending
                                const toStore = (distanceUnit === 'mi') ? milesToKm(value) : value;
                                handleSetChange(set._id, { [col.field]: toStore });
                              }}
                              disabled={isAdding}
                              editable={editable}
                              clearWorkoutCoachOnFocus
                            />
                          );
                        }

                        return (
                          <Input
                              preserveFocusedDraft
                            value={col.unitKind === 'elevation' && elevationUnit === 'ft'
                              ? metersToFeet(set[col.field])
                              : set[col.field]}
                            min={col.min}
                            max={col.max}
                            step={col.step}
                            type={col.type}
                            className={`${col.field}-field`}
                            onChange={(value) => {
                              const toStore = col.unitKind === 'elevation' && elevationUnit === 'ft'
                                ? feetToMeters(value)
                                : value;
                              handleSetChange(set._id, { [col.field]: toStore });
                            }}
                            disabled={isAdding}
                            editable={editable}
                            clearWorkoutCoachOnFocus
                          />
                        );
                      })()
                    )}
                  </td>
                ))}
                {showSetActions && (
                  <td
                    key={`${workout_exercise._id}-${index}-action`}
                    className="exercise-set-cell exercise-set-action"
                    data-label="Remove"
                    aria-label="Remove set"
                    style={{ textAlign: "center" }}
                  >
                    <button
                      aria-label="Remove set"
                      className="trash-button"
                      onClick={() => handleRemoveWorkoutSet(set._id)}
                      disabled={isAdding}
                    >
                      <TrashIcon />
                    </button>
                  </td>
                )}
              </tr>
              </React.Fragment>
            );
            })}
            {!structureLocked && canAddWorkoutSet({
              readOnly,
              workoutEditable: isWorkoutEditable(workout),
              isOngoing,
              isTemplateMode
            }) && (
              <tr key="add-set-row" className="exercise-set-add-row">
                <td
                  colSpan={columns.length + (showSetActions ? 1 : 0)}
                  className="exercise-set-cell"
                >
                  <button
                    className="btn-add-set-modern"
                    onClick={handleAddWorkoutSet}
                    disabled={isAdding}
                  >
                    <PlusIcon style={{ width: 20, height: 20 }} />
                    Add Set
                  </button>
                  {addSetError && <div role="alert">{addSetError}</div>}
                  <div>{children}</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default WorkoutExercise;
